// ============================================================
// fondos/efectos/mesh-gradient.js — «Mesh Flow»: una malla de color A SANGRE que fluye (efecto de
// LIENZO a resolución BAJA)
//
// Portado de `vendor/bg/mesh-gradient-bg.js` en la F3 del P3 (§3.1-bis, 30-09-2026): un lavado de los
// dos colores sobre TODA la sección y, encima, 4 focos grandes (3 en táctil) que orbitan y se funden,
// cada uno un degradado radial del tamaño de la sección entera. Es el único fondo que tiñe la sección
// de borde a borde, sin zonas en blanco: esa es su firma (en capas, sus focos sueltos eran la misma
// nube que humo y fluido, «nula diferencia», el dueño el 30-09). El lienzo va a ⅙ de la resolución
// de la página (`nitidez: 'baja'`; la clase de siempre iba a ¼, y a ⅙ costaba 4,3 % de hilo frente a
// 6,3 % sin que se note): el degradado no necesita más.
// Respecto a ella:
//   · el segundo color es el de la PALETA (`params.color2`), no un blanco o un marino fijos (E4), y
//     los focos se cruzan en diagonal (acento arriba a la izquierda y abajo a la derecha);
//   · las alfas son fracciones del tope del catálogo (`alfaMax`, legibilidad);
//   · las órbitas duran 13-31 s a la velocidad por defecto (antes 55-126 s: no se veía moverse);
//   · la SUAVIDAD (`blur`) abre los focos: más grandes y con el núcleo menos marcado.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, sin DOM, sin reloj y sin estado de módulo.
// ============================================================

import { rgbDeBase, rgba, interpolar } from './comun.js';
import { crecePorSuavidad } from './manchas.js';

/** Alfa del lavado de toda la sección (fracción del tope). */
const LAVADO = 0.3;

/** Radio de un foco (× el lado mayor de la sección). */
const RADIO = 0.68;

export default {
    nitidez: 'baja',
    divisor: 6,

    crear(ctx, e) {
        const p = e.params;
        // Velocidad 1-10 → 0,12-0,6 rad/s de órbita (cada foco, entre ×0,6 y ×1,4).
        const vel = interpolar(p.speed, 5, 0.12, 0.6);
        const c1 = rgbDeBase(e.c1);
        const c2 = p.color2 ? rgbDeBase(p.color2) : c1;
        const s = typeof p.blur === 'number' && p.blur > 0 ? Math.min(p.blur, 100) / 100 : 0;
        const focos = [];
        for (let i = 0; i < (e.tactil ? 3 : 4); i++) {
            focos.push({
                cx: 0.2 + 0.6 * (i % 2),
                cy: 0.25 + 0.5 * (i < 2 ? 0 : 1),
                ox: 0.18 + e.azar() * 0.12,
                oy: 0.15 + e.azar() * 0.12,
                a: e.azar() * Math.PI * 2,
                vel: vel * (0.6 + e.azar() * 0.8) * (i % 2 ? -1 : 1),
                // En diagonal: acento en (0, 3), segundo color en (1, 2).
                color: (i + Math.floor(i / 2)) % 2 ? c2 : c1,
            });
        }
        return { w: e.w, h: e.h, alfa: e.alfa, c1: c1, c2: c2, focos: focos,
                 crece: crecePorSuavidad(p.blur), medio: 0.4 + 0.15 * s };
    },

    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
    },

    dibujar(s, ctx, t) {
        const time = t * 0.001;
        const W = s.w;
        const H = s.h;
        // El lavado: toda la sección, del acento al segundo color.
        const lavado = ctx.createLinearGradient(0, 0, W, H);
        lavado.addColorStop(0, rgba(s.c1, s.alfa * LAVADO));
        lavado.addColorStop(1, rgba(s.c2, s.alfa * LAVADO));
        ctx.fillStyle = lavado;
        ctx.fillRect(0, 0, W, H);

        const R = Math.max(W, H) * RADIO * s.crece;
        s.focos.forEach(function (f) {
            const px = (f.cx + Math.cos(f.a + time * f.vel) * f.ox) * W;
            const py = (f.cy + Math.sin(f.a + time * f.vel) * f.oy) * H;
            const g = ctx.createRadialGradient(px, py, 0, px, py, R);
            g.addColorStop(0, rgba(f.color, s.alfa));
            g.addColorStop(0.35, rgba(f.color, s.alfa * s.medio));
            g.addColorStop(1, rgba(f.color, 0));
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, W, H);
        });
    },
};
